import { useState, useRef, useEffect } from 'react';
import { MessageSquare, Plus, Trash2, Pencil, Check, X, ChevronDown, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/useAuthStore';
import type { ChatSession } from '@/types/chat';
import styles from './SessionDropdown.module.css';

interface SessionDropdownProps {
  sessions: ChatSession[];
  activeSessionId: string | null;
  isLoading: boolean;
  hasNextPage: boolean;
  onSelectSession: (id: string) => void;
  onNewSession: () => void;
  onDeleteSession: (id: string) => void;
  onRenameSession: (id: string, title: string) => void;
  onLoadMore: () => void;
}

export function SessionDropdown({
  sessions,
  activeSessionId,
  isLoading,
  hasNextPage,
  onSelectSession,
  onNewSession,
  onDeleteSession,
  onRenameSession,
  onLoadMore,
}: SessionDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const currentUserEmail = useAuthStore((s) => s.user?.email);
  const isAdmin = useAuthStore((s) => s.isAdmin());

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setEditingId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filterLower = filter.toLowerCase();
  const filtered = sessions.filter((s) => {
    if (s.title.toLowerCase().includes(filterLower)) return true;
    if (isAdmin && s.owner_email?.toLowerCase().includes(filterLower)) return true;
    if (isAdmin && s.owner_name?.toLowerCase().includes(filterLower)) return true;
    return false;
  });

  const activeSession = sessions.find((s) => s.id === activeSessionId);

  const startEdit = (session: ChatSession, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(session.id);
    setEditTitle(session.title);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const confirmEdit = (id: string, e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (editTitle.trim()) {
      onRenameSession(id, editTitle.trim());
    }
    setEditingId(null);
  };

  const cancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingId(null);
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        className={styles.triggerBtn}
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
      >
        <MessageSquare size={15} />
        <span className={styles.triggerLabel}>
          {activeSession ? activeSession.deal_title || activeSession.title : 'Chats'}
        </span>
        <ChevronDown size={14} className={isOpen ? styles.chevronOpen : styles.chevron} />
      </button>

      {isOpen && (
        <div className={styles.panel}>
          {/* New Chat Button */}
          <button
            className={styles.newBtn}
            onClick={() => {
              onNewSession();
              setIsOpen(false);
            }}
          >
            <Plus size={14} />
            New Chat
          </button>

          {/* Filter Input */}
          <div className={styles.filterWrapper}>
            <input
              className={styles.filterInput}
              placeholder="Search chats..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>

          {/* Sessions List */}
          <div className={styles.list}>
            {isLoading && sessions.length === 0 && (
              <div className={styles.loadingRow}>
                <Loader2 size={16} className={styles.spinner} />
                <span>Loading...</span>
              </div>
            )}

            {!isLoading && filtered.length === 0 && (
              <p className={styles.emptyMsg}>No chats found.</p>
            )}

            {filtered.map((session) => {
              const isActive = session.id === activeSessionId;
              const isEditing = editingId === session.id;
              const isForeign = isAdmin && !!session.owner_email && session.owner_email !== currentUserEmail;

              return (
                <div
                  key={session.id}
                  className={`${styles.sessionRow} ${isActive ? styles.active : ''}`}
                  onClick={() => {
                    if (!isEditing) {
                      onSelectSession(session.id);
                      setIsOpen(false);
                    }
                  }}
                >
                  {isEditing ? (
                    <div className={styles.editRow} onClick={(e) => e.stopPropagation()}>
                      <input
                        ref={inputRef}
                        className={styles.editInput}
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') confirmEdit(session.id, e);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                      />
                      <button
                        className={styles.iconBtn}
                        onClick={(e) => confirmEdit(session.id, e)}
                      >
                        <Check size={13} />
                      </button>
                      <button className={styles.iconBtn} onClick={cancelEdit}>
                        <X size={13} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className={styles.sessionInfo}>
                        <span className={styles.sessionTitle}>
                          {session.deal_title && <span className={styles.dealBadge}>Deal</span>}
                          {session.deal_title || session.title}
                        </span>
                        <span className={styles.sessionMeta}>
                          {formatDate(session.updated_at)}
                          {session.message_count != null && ` · ${session.message_count} msgs`}
                        </span>
                        {isAdmin && session.owner_email && (
                          <span className={styles.ownerBadge} title={session.owner_email}>
                            {session.owner_name || session.owner_email}
                          </span>
                        )}
                      </div>
                      {!isForeign && (
                        <div className={styles.rowActions}>
                          <button
                            className={styles.iconBtn}
                            title="Rename"
                            onClick={(e) => startEdit(session, e)}
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            className={`${styles.iconBtn} ${styles.deleteBtn}`}
                            title="Delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDeleteSession(session.id);
                            }}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}

            {hasNextPage && (
              <button className={styles.loadMoreBtn} onClick={onLoadMore}>
                Load more
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
